import { gql, useQuery } from '@apollo/client'
import { Stack, Typography } from '@mui/material'
import {
  Campaign,
  Query,
  Retailer,
  StatsShare,
} from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import moment from 'moment'
import { useMemo } from 'react'
import { useHistory } from 'react-router'
import sanitizeHtml from 'sanitize-html'

import analytics from '@src/analytics'
import { statsCounter } from '@src/components/reporting/statsTotals'
import { buildShareDialogHref } from '@src/components/shareDialog/useShareDialog'
import { ThemeAwareColor } from '@src/lib/theme'
import useCurrentRetailerAccount from '@src/lib/useCurrentRetailerAccount'

import {
  CampaignCardGridContainer,
  CampaignCardGridItem,
  CampaignCardIconCarousel,
  CampaignCardIconPicture,
  CampaignCardIconVideo,
  CampaignCardQuickStat,
  CampaignCardSkeleton,
  CampaignCardTruncatedDescription,
} from '../CampaignCard'
import { OmniCampaignCard } from '../OmniCampaignCard'

import { OrganicCampaignDetailsFilters } from './organicCampaignDetailsFilter'
import { buildShareSettingDialogHref } from './useShareSettingDialog'
interface OrganicCampaignDetailsShareSettingsProps {
  campaignId: Campaign['id']
  shareType: string
  filterApi: OrganicCampaignDetailsFilters
  retailerId?: Retailer['id'] | null
}

const OrganicCampaignDetailsShareSettings: React.FC<
  OrganicCampaignDetailsShareSettingsProps
> = (props) => {
  const history = useHistory()
  const { ensureCurrentRetailerAccount } = useCurrentRetailerAccount()
  const { data, loading } = useQuery<Query>(GET_SHARE_SETTINGS_QUERY, {
    variables: {
      retailer_id: props.retailerId,
      retailer_ids:
        ensureCurrentRetailerAccount.retailers?.map((x) => x.id) || [],
      campaign_id: props.campaignId,
      share_type: props.shareType,
      channel: props.shareType,
    },
    skip: props.retailerId == null,
  })

  const { data: sharesData } = useQuery<Query>(GET_SHARES_QUERY, {
    variables: {
      retailer_id: props.retailerId,
      retailer_ids:
        ensureCurrentRetailerAccount.retailers?.map((x) => x.id) || [],
      campaign_id: props.campaignId,
      share_type: props.shareType,
      channel: props.shareType,
    },
    skip: props.retailerId == null || props.shareType === 'embed',
  })

  const filteredShareSettings = useMemo(() => {
    if (data == null) {
      return []
    }

    const sorted = [...data.getShareSettingsRetailer]
    sorted.sort((a, b) => {
      if (props.filterApi.debouncedFilterInfo.filter.sortField === 'created') {
        return (
          new Date(a.created_at || '').valueOf() -
          new Date(b.created_at || '').valueOf()
        )
      }

      if (props.filterApi.debouncedFilterInfo.filter.sortField === 'updated') {
        return (
          new Date(a.updated_at || '').valueOf() -
          new Date(b.updated_at || '').valueOf()
        )
      }

      if (props.filterApi.debouncedFilterInfo.filter.sortField === 'name') {
        return (a.name || '').localeCompare(b.name || '')
      }

      if (props.filterApi.debouncedFilterInfo.filter.sortField === 'format') {
        return (
          a.digital_assets?.[0]?.file?.format?.localeCompare(
            b.digital_assets?.[0]?.file?.format || '',
          ) || 0
        )
      }

      return 0
    })

    if (props.filterApi.debouncedFilterInfo.filter.sortDirection === 'desc') {
      return sorted.reverse()
    }

    return sorted
  }, [
    data,
    props.filterApi.debouncedFilterInfo.filter.sortDirection,
    props.filterApi.debouncedFilterInfo.filter.sortField,
  ])

  const { filteredShares, alreadySharedShareSettingIds } = useMemo(() => {
    const filteredShares = sharesData?.getShares?.data || []
    const alreadySharedShareSettingIds = filteredShares.map(
      (x) => x.share_setting?.id,
    )

    return {
      filteredShares,
      alreadySharedShareSettingIds,
    }
  }, [sharesData?.getShares.data])

  const channelColor = CHANNEL_COLORS[props.shareType] || 'primary'

  if (loading || !props.retailerId) {
    return (
      <>
        <CampaignCardGridContainer>
          <CampaignCardGridItem>
            <CampaignCardSkeleton />
          </CampaignCardGridItem>
          <CampaignCardGridItem>
            <CampaignCardSkeleton />
          </CampaignCardGridItem>
          <CampaignCardGridItem>
            <CampaignCardSkeleton />
          </CampaignCardGridItem>
          <CampaignCardGridItem>
            <CampaignCardSkeleton />
          </CampaignCardGridItem>
        </CampaignCardGridContainer>
      </>
    )
  }

  return (
    <>
      <CampaignCardGridContainer>
        {filteredShareSettings.map((x) => {
          const wasAlreadyShared =
            alreadySharedShareSettingIds?.includes(x.id) || false

          const description =
            props.shareType === 'email'
              ? sanitizeHtml(x.description || '', { allowedTags: [] })
              : x.retailer_description

          let media =
            x.digital_assets?.map((da) => ({
              publicId: da?.file?.public_id,
              type: da?.file?.resource_type,
              externalVideoUrl: da?.external_video_url,
            })) || []

          //Why does instagram, which does not have carousels yet, have multiple assets?
          if (x.post_format !== 'carousel') {
            media = media.slice(0, 1)
          }

          let mediaTypeIcon = <CampaignCardIconPicture color={channelColor} />
          if (media) {
            if (media?.length > 1) {
              mediaTypeIcon = <CampaignCardIconCarousel color={channelColor} />
            } else if (
              media?.[0]?.type === 'video' ||
              media?.[0]?.externalVideoUrl
            ) {
              mediaTypeIcon = <CampaignCardIconVideo color={channelColor} />
            }
          }

          return (
            <CampaignCardGridItem key={x.id}>
              <OmniCampaignCard
                title={x.name}
                description={
                  <CampaignCardTruncatedDescription>
                    {description}
                  </CampaignCardTruncatedDescription>
                }
                media={media}
                secondaryContent={mediaTypeIcon}
                actionAreaLabel={
                  wasAlreadyShared
                    ? I18n.t('shareCard.shareAgain')
                    : I18n.t('shareCard.previewAndShare')
                }
                actionAreaColor={channelColor}
                dim={wasAlreadyShared}
                actionAreaInvert={wasAlreadyShared}
                onClick={() => {
                  analytics.track({
                    event: 'Click',
                    type: 'preview_share_setting',
                    data: {
                      card_channel: x.share_type,
                      campaign_id: x.campaign?.id,
                      campaign_brand_id: x.campaign?.brand?.id,
                      campaign_brand_name: x.campaign?.brand?.name,
                      campaign_type: x.campaign?.campaign_type,
                      co_op_enabled: x.campaign?.co_op_enabled,
                    },
                  })
                  history.push(
                    buildShareSettingDialogHref(
                      x.id,
                      x.campaign?.brand?.id || '',
                    ),
                  )
                }}
              />
            </CampaignCardGridItem>
          )
        })}
      </CampaignCardGridContainer>

      {filteredShares.length > 0 ? (
        <>
          <Typography variant="h6" sx={{ marginY: 4 }}>
            {I18n.t('content.organicCampaignDetails.previouslyShared')}
          </Typography>

          <CampaignCardGridContainer>
            {filteredShares.map((x) => {
              const description =
                props.shareType === 'email'
                  ? sanitizeHtml(x.description || '', { allowedTags: [] })
                  : x.description

              let statsForShare: StatsShare | undefined = undefined
              for (const stat of sharesData?.getCampaign?.getStats.stats ||
                []) {
                for (const statsShare of stat.data?.shares || []) {
                  if (String(statsShare.share_id) === String(x.id)) {
                    statsForShare = statsShare
                    break
                  }
                }
              }

              let media =
                x.share_setting?.digital_assets?.map((da) => ({
                  publicId: da?.file?.public_id,
                  type: da?.file?.resource_type,
                  externalVideoUrl: da?.external_video_url,
                })) || []

              if (x.share_setting?.post_format !== 'carousel') {
                media = media.slice(0, 1)
              }

              const quickStats =
                x.status === 'scheduled' ? (
                  <>
                    <Typography color="text.secondary" textAlign="center">
                      {I18n.t('reporting.scheduledFor', {
                        date: moment(x.scheduled_for).format('LLLL'),
                      })}
                    </Typography>
                  </>
                ) : (
                  <Stack direction="row" justifyContent="space-around">
                    <CampaignCardQuickStat
                      number={statsCounter(statsForShare?.views) || 0}
                      label={I18n.t('shared.views')}
                    />
                    <CampaignCardQuickStat
                      number={statsCounter(statsForShare?.actions) || 0}
                      label={I18n.t('shared.actions')}
                    />
                    <CampaignCardQuickStat
                      number={statsCounter(statsForShare?.clicks) || 0}
                      label={I18n.t('shared.clicks')}
                    />
                  </Stack>
                )

              return (
                <CampaignCardGridItem key={x.id}>
                  <OmniCampaignCard
                    title={x.share_setting?.name}
                    description={
                      <>
                        <CampaignCardTruncatedDescription>
                          {description}
                        </CampaignCardTruncatedDescription>

                        {quickStats}
                      </>
                    }
                    media={media}
                    href={buildShareDialogHref(
                      x.id,
                      `${props.shareType}_shares`,
                    )}
                    actionAreaLabel={
                      x.status === 'scheduled'
                        ? I18n.t('shared.edit')
                        : I18n.t('shareCard.viewReporting')
                    }
                    actionAreaColor={channelColor}
                    actionAreaInvert
                  />
                </CampaignCardGridItem>
              )
            })}
          </CampaignCardGridContainer>
        </>
      ) : undefined}
    </>
  )
}

const CHANNEL_COLORS: Record<string, ThemeAwareColor> = {
  facebook: 'facebook',
  instagram: 'instagram',
  // downloadables: 'downloadables',
  twitter: 'twitter',
  tiktok: 'tiktok',
  // banner: 'banner',
  // email: 'email',
}

const GET_SHARE_SETTINGS_QUERY = gql`
  query getShareSettingsOrganicDetails(
    $retailer_id: ID!
    $campaign_id: ID!
    $share_type: String!
  ) {
    getShareSettingsRetailer(
      retailer_id: $retailer_id
      campaign_id: $campaign_id
      filters: { share_type: $share_type }
      fields: ["digital_assets", "campaign.brand"]
    ) {
      id
      name
      share_type
      description
      created_at
      updated_at
      retailer_body
      retailer_subject
      retailer_description
      post_format

      campaign {
        id
        co_op_enabled
        campaign_type
        brand {
          name
          id
        }
      }
      digital_assets {
        id
        file {
          id
          public_id
          format
          resource_type
        }
        external_video_url
      }
    }
  }
`

const GET_SHARES_QUERY = gql`
  query getSharesYup(
    $retailer_id: ID!
    $retailer_ids: [ID!]!
    $campaign_id: ID!
    $channel: String!
  ) {
    getShares(
      params: {
        retailer_id: $retailer_id
        campaign_id: $campaign_id
        fields: ["share_setting", "share_setting.digital_assets"]
        channels: [$channel]
        status: ["posting", "scheduled", "success"]
      }
    ) {
      data {
        id
        status
        created_at
        scheduled_for

        retailer_id
        facebook_page_id

        share_setting {
          id
          name
          share_type
          description
          created_at
          updated_at
          post_format
          digital_assets {
            id
            file {
              id
              public_id
              format
              resource_type
            }
            external_video_url
          }
        }
      }
    }

    # It seems silly to re-query the campaign here, but all this component gets
    # is the slug version, where stats needs the numerical ID.
    # We could query that in the parent component, but that creates a waterfall
    # of fetching.
    # So, here we are.
    getCampaign(campaignId: $campaign_id) {
      id

      getStats(params: { retailer_ids: $retailer_ids }) {
        stats {
          brand_id

          data {
            campaign_id

            shares {
              share_id
              channel

              actions {
                post_reaction_like
                post_reaction_haha
                post_reaction_angry
                post_reaction_sad
                post_reaction_love
                post_reaction_wow
                clicks
                video_views
                comments
                photo_views
                posts
                rsvps
                # twitter
                retweets
                favorites
                # instagram
                engagements
                saved
              }

              clicks {
                clicks
                clicked
              }

              views {
                impressions_organic
                impressions_paid
                opened
              }

              email {
                delivered
                processed
                deferred
                bounced
                dropped
              }

              instagram {
                impressions
                saved
                reach
                engagements
              }
            }
          }

          filter_id
          retailer_id
          stat_type
          updated_at
        }
      }
    }
  }
`

export default OrganicCampaignDetailsShareSettings
